import { useMemo } from 'react';
import { isAfter, parseISO } from 'date-fns';
import { useUserContributorsQuery, useUserContributorsListQuery } from '@/state/queries/contributors/user-contributors';
import type { EventSummaryDto } from '@/types/dtos/events/response/event-summary.dto';
import type { EventListSummaryDto } from '@/types/dtos/contributors/response/event-list-summary.dto';
import type { ContributorSummaryDto } from '@/types/dtos/contributors/response/contributor-summary.dto';
import type { ContributorListSummaryDto } from '@/types/dtos/contributors/response/contributor-list-summary.dto';

type UseContributionsOptions = {
  searchTerm?: string;
  useCarousel?: boolean;
  useListMode?: boolean; // Use lightweight list endpoint
};

type EventForContributions = EventSummaryDto | EventListSummaryDto;

const isUpcomingContributor = (event: EventForContributions | undefined, now: Date) => {
  if (!event?.startDate) return false;

  const startDate = parseISO(event.startDate);

  if (event.durationDays) {
    const endDate = new Date(startDate);
    endDate.setDate(endDate.getDate() + event.durationDays - 1);
    return isAfter(endDate, now) || isAfter(now, startDate);
  }

  return isAfter(startDate, now) || startDate.toDateString() === now.toDateString();
};

const matchesSearch = (event: EventForContributions | undefined, searchTerm: string) => {
  if (!searchTerm.trim()) return true;
  const searchLower = searchTerm.toLowerCase();
  const eventTitle = event?.title?.toLowerCase() || '';
  const eventDescription = event?.description?.toLowerCase() || '';
  const eventLocation = event?.location?.toLowerCase() || '';

  return (
    eventTitle.includes(searchLower) || eventDescription.includes(searchLower) || eventLocation.includes(searchLower)
  );
};

export const useContributions = ({
  searchTerm = '',
  useCarousel = false,
  useListMode = false,
}: UseContributionsOptions = {}) => {
  const { data: userContributors, isLoading: isLoadingFull } = useUserContributorsQuery();
  const { data: userContributorsList, isLoading: isLoadingList } = useUserContributorsListQuery();

  const isLoading = useListMode ? isLoadingList : isLoadingFull;
  const pages = useListMode ? (userContributorsList?.pages ?? []) : (userContributors?.pages ?? []);
  const allContributions: (ContributorSummaryDto | ContributorListSummaryDto)[] = pages.flatMap(
    (page) => page.data as (ContributorSummaryDto | ContributorListSummaryDto)[],
  );

  const upcomingContributions = useMemo(() => {
    if (!useCarousel) return [];
    const now = new Date();
    return allContributions.filter((contributor) => {
      const event = contributor.event as EventForContributions | undefined;
      return isUpcomingContributor(event, now);
    });
  }, [allContributions, useCarousel]);

  const sortedUpcomingContributions = useMemo(() => {
    if (!useCarousel) return [];
    return [...upcomingContributions]
      .sort((a, b) => {
        const dateA = a.event?.startDate ? parseISO(a.event.startDate).getTime() : 0;
        const dateB = b.event?.startDate ? parseISO(b.event.startDate).getTime() : 0;
        return dateA - dateB;
      })
      .slice(0, 5);
  }, [upcomingContributions, useCarousel]);

  const contributions = useMemo(() => {
    const sourceContributions = useCarousel ? sortedUpcomingContributions : allContributions;

    if (!searchTerm.trim()) {
      return sourceContributions;
    }

    return sourceContributions.filter((contributor) =>
      matchesSearch(contributor.event as EventForContributions | undefined, searchTerm),
    );
  }, [allContributions, sortedUpcomingContributions, searchTerm, useCarousel]);

  return {
    isLoading,
    allContributions,
    contributions,
    upcomingContributions: sortedUpcomingContributions,
    hasAnyContributions: allContributions.length > 0,
  };
};
